import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  GetConversions,
  GetConversionsList,
  GetAdInfo,
  GetSpend,
  GetMarketingDashboard,
  GetPostbackControls,
  SetPostbackControls,
  RunPostbacks,
  GetPostbackLog,
  GetPostbackRuns,
  GetAttributionWaterfall,
  GetTraffic,
  GetQuizFunnel,
  RepairNotSetTouches,
  GetFollowUpControls,
  SetFollowUpControls,
  RunFollowUp,
} from "Services/API/Conversions.service";

/**
 * Conversions funnel + Meta spend in ONE request (react-query). A page load /
 * range change / grouping change fires a single call, and the backend runs one
 * shared funnel pass instead of three. Returns { rows, totals, spend, ... };
 * spend keeps the shape the dashboard's spend UI expects.
 */
export function useMarketingDashboard({ from, to, groupBy, enabled = true }) {
  const { data, isLoading, isFetching, isError, error, refetch } = useQuery(
    ["marketing-dashboard", from, to, groupBy],
    ({ signal }) => GetMarketingDashboard({ from, to, groupBy }, signal),
    { keepPreviousData: true, enabled }
  );

  const conversions = data?.conversions ?? null;
  const spend = data?.spend ?? null;

  return {
    rows: conversions?.data ?? [],
    totals: conversions?.totals ?? null,
    // Same shape useSpend returned, so the spend UI is untouched.
    spend: {
      configured: spend?.configured === true,
      totals: spend?.totals ?? null,
      campaigns: spend?.campaigns ?? [],
      ads: spend?.ads ?? [],
      unmatched: spend?.unmatched ?? null,
    },
    isLoading,
    // keepPreviousData means isLoading is false on every refetch after the
    // first — the previous range's rows stay on screen. isFetching is the only
    // signal that a request is in flight, so it is what the UI must show.
    isFetching,
    isError,
    error,
    refetch,
  };
}

/**
 * Conversions funnel data (react-query). Refetches when the date range,
 * grouping, or channel scope changes; keeps the previous table on screen
 * while loading. `tab` scopes the funnel to one channel (per-campaign cards);
 * omit it for the all-channel summary. Kept alongside useMarketingDashboard for
 * the per-tab campaign filter, which needs a channel-scoped funnel.
 */
export function useConversions({ from, to, groupBy, tab, enabled = true }) {
  const { data, isLoading, isFetching, isError, error, refetch } = useQuery(
    ["conversions", from, to, groupBy, tab ?? null],
    ({ signal }) => GetConversions({ from, to, groupBy, tab: tab || undefined }, signal),
    { keepPreviousData: true, enabled }
  );

  return {
    rows: data?.data ?? [],
    totals: data?.totals ?? null,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  };
}

/**
 * Paginated detail rows for one channel tab of the conversions dashboard,
 * conversions first. `campaign` narrows it to a single campaign of that tab
 * (the campaign strip's selection). keepPreviousData keeps the current page
 * rendered while the next loads.
 */
export function useConversionsList({ tab, page, perPage, from, to, campaign, enabled = true }) {
  const { data, isLoading, isFetching, isError, error } = useQuery(
    ["conversions-list", tab, page, perPage, from, to, campaign ?? null],
    ({ signal }) =>
      GetConversionsList({
        tab,
        page,
        perPage,
        from: from || undefined,
        to: to || undefined,
        campaign: campaign || undefined,
      }, signal),
    { keepPreviousData: true, enabled }
  );

  return {
    rows: data?.data ?? [],
    total: data?.total ?? 0,
    isLoading,
    isFetching,
    isError,
    error,
  };
}

/**
 * Meta spend joined to the funnel for the selected range. configured stays
 * false (and everything empty) when the backend has no Meta credentials, so
 * the dashboard can omit the spend columns entirely.
 */
export function useSpend({ from, to, enabled = true }) {
  const { data, isFetching } = useQuery(
    ["spend", from, to],
    ({ signal }) => GetSpend({ from, to }, signal),
    { keepPreviousData: true, enabled },
  );
  return {
    isFetching,
    configured: data?.configured === true,
    totals: data?.totals ?? null,
    campaigns: data?.campaigns ?? [],
    ads: data?.ads ?? [],
    unmatched: data?.unmatched ?? null,
  };
}

/**
 * Meta campaign/ad names + delivery status for the numeric ids visible in
 * the current table page. Cached an hour; no-ops with an empty id list or
 * when the backend has no Meta token.
 */
export function useAdInfo(ids) {
  const key = [...ids].sort().join(",");
  const { data } = useQuery(["ad-info", key], () => GetAdInfo(key), {
    enabled: ids.length > 0,
    staleTime: 60 * 60 * 1000,
  });
  return data ?? {};
}

/**
 * Postback switches + last-run summary, with mutations for toggling a
 * platform and triggering a run. Toggling refreshes the controls; a run also
 * refreshes them (the run records itself as lastRun server-side).
 */
/**
 * "(not set)" touch repair. Dry run previews the affected records; the live
 * run pulls the junk touches and invalidates the funnel/list caches so the
 * repaired campaign grouping shows without a page reload.
 */
export function useTouchRepair() {
  const queryClient = useQueryClient();
  const repair = useMutation((dryRun) => RepairNotSetTouches(dryRun), {
    onSuccess: (result) => {
      if (!result?.dryRun) {
        queryClient.invalidateQueries(["conversions"]);
        queryClient.invalidateQueries(["conversions-list"]);
      }
    },
  });
  return repair;
}

export function usePostbackControls() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries(["postback-controls"]);

  const { data, isLoading } = useQuery(["postback-controls"], GetPostbackControls);

  const setControls = useMutation((body) => SetPostbackControls(body), {
    onSuccess: (fresh) => queryClient.setQueryData(["postback-controls"], fresh),
  });

  const run = useMutation((dryRun) => RunPostbacks(dryRun), {
    onSettled: () => {
      invalidate();
      queryClient.invalidateQueries(["postback-runs"]);
      queryClient.invalidateQueries(["postback-log"]);
    },
  });

  return {
    controls: data ?? null,
    isLoading,
    setControls,
    run,
  };
}

/** Paginated postback delivery ledger for the Postbacks tab. */
export function usePostbackLog({ page, perPage, enabled = true }) {
  const { data, isLoading } = useQuery(
    ["postback-log", page, perPage],
    () => GetPostbackLog({ page, perPage }),
    { keepPreviousData: true, enabled }
  );
  return {
    rows: data?.data ?? [],
    total: data?.total ?? 0,
    isLoading,
  };
}

/** Paginated postback run history for the Postbacks tab's run table. */
export function usePostbackRuns({ page, perPage, enabled = true }) {
  const { data, isLoading } = useQuery(
    ["postback-runs", page, perPage],
    () => GetPostbackRuns({ page, perPage }),
    { keepPreviousData: true, enabled }
  );
  return {
    rows: data?.data ?? [],
    total: data?.total ?? 0,
    isLoading,
  };
}

/** Attribution waterfall for the Attribution tab. Read-only. */
export function useAttributionWaterfall({ days = 14, enabled = true }) {
  const { data, isLoading, isFetching, isError } = useQuery(
    ["attribution-waterfall", days],
    ({ signal }) => GetAttributionWaterfall(days, signal),
    { enabled, keepPreviousData: true },
  );
  return { data, isLoading, isFetching, isError };
}

/**
 * First-party traffic report for the Traffic tab. Shares the dashboard's date
 * range, so switching tabs keeps the same window.
 */
export function useTraffic({ from, to, enabled = true }) {
  const { data, isLoading, isFetching, isError } = useQuery(
    ["traffic", from, to],
    ({ signal }) => GetTraffic({ from: from || undefined, to: to || undefined }, signal),
    { enabled, keepPreviousData: true },
  );
  return { data, isLoading, isFetching, isError };
}

/**
 * Quiz funnel keyed like the conversion rows ("" for the null bucket), so a
 * row's quiz numbers are one Map lookup. Refetches with range / tab.
 */
export function useQuizFunnel({ from, to, groupBy, tab, enabled = true }) {
  const { data, isFetching, isError } = useQuery(
    ["quiz-funnel", from, to, groupBy, tab ?? null],
    ({ signal }) =>
      GetQuizFunnel({ from: from || undefined, to: to || undefined, groupBy, tab: tab || undefined }, signal),
    { enabled, keepPreviousData: true },
  );
  const byKey = useMemo(
    () => new Map((data?.rows ?? []).map((r) => [String(r.key ?? ""), r])),
    [data],
  );
  return { byKey, totals: data?.totals ?? null, isFetching, isError };
}

/**
 * Lapsed-registration follow-up: controls plus manual/dry runs.
 *
 * A live run changes the funnel (it grants discounts and stamps offers), so it
 * invalidates the conversions queries — the "Offer sent" column reflects the
 * run without a page reload. A dry run touches nothing and invalidates nothing.
 */
export function useFollowUpOffer({ enabled = true } = {}) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery(["followup-controls"], GetFollowUpControls, { enabled });

  const setControls = useMutation((body) => SetFollowUpControls(body), {
    onSuccess: (fresh) => queryClient.setQueryData(["followup-controls"], fresh),
  });

  const run = useMutation((dryRun) => RunFollowUp(dryRun), {
    onSuccess: (result) => {
      if (!result?.dryRun) {
        queryClient.invalidateQueries(["conversions"]);
        queryClient.invalidateQueries(["conversions-list"]);
      }
    },
    onSettled: () => queryClient.invalidateQueries(["followup-controls"]),
  });

  return { controls: data ?? null, isLoading, setControls, run };
}
